import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI, api } from '../services/api';
import toast from 'react-hot-toast';
import { USER_DATA_KEY, SESSION_INACTIVITY_TIMEOUT_MS, SESSION_INACTIVITY_WARNING_BEFORE_MS } from '../constants';
import { setupAxiosInterceptors } from '../utils/axiosConfig';
import i18n from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [forceLogoutInfo, setForceLogoutInfo] = useState(null);
  const [pendingTc, setPendingTc] = useState(null); // v3.22.0
  const [pendingAnnouncements, setPendingAnnouncements] = useState(null); // v3.23.0
  const [passwordExpired, setPasswordExpired] = useState(false);

  // Define clearAuth early so it can be used in useEffect
  const clearAuth = () => {
    localStorage.removeItem(USER_DATA_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  // ========== Session Inactivity Tracker ==========
  const lastActivityRef = useRef(Date.now());
  const inactivityTimerRef = useRef(null);
  const warningShownRef = useRef(false);

  const resetActivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  }, []);

  // Track user activity events
  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    const handleActivity = () => { resetActivityTimer(); };

    activityEvents.forEach(event => window.addEventListener(event, handleActivity, { passive: true }));

    // Check inactivity every 60 seconds
    inactivityTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const timeUntilExpiry = SESSION_INACTIVITY_TIMEOUT_MS - elapsed;

      // Show warning 15 min before expiry
      if (timeUntilExpiry <= SESSION_INACTIVITY_WARNING_BEFORE_MS && timeUntilExpiry > 0 && !warningShownRef.current) {
        warningShownRef.current = true;
        const minutesLeft = Math.ceil(timeUntilExpiry / 60000);
        toast(i18n.t('auth:sessionInactivityWarning', { minutes: minutesLeft }), {
          icon: '\u26a0\ufe0f',
          duration: 10000,
        });
      }

      // Auto-logout when timeout reached
      if (elapsed >= SESSION_INACTIVITY_TIMEOUT_MS) {
        clearAuth();
        toast.error(i18n.t('auth:sessionInactivityExpired'));
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }, 60000);

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
    };
  }, [isAuthenticated, resetActivityTimer]); // eslint-disable-line react-hooks/exhaustive-deps
  // ========== End Session Inactivity Tracker ==========

  // Setup axios interceptors for 401 errors
  useEffect(() => {
    setupAxiosInterceptors(api, (isInactivity) => {
      // On 401 error, clear auth state
      clearAuth();
      toast.error(isInactivity ? i18n.t('auth:sessionInactivityExpired') : i18n.t('auth:sessionExpired'));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Verificar token al cargar la aplicación
  useEffect(() => {
    checkAuthStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuthStatus = async () => {
    try {
      const userData = localStorage.getItem(USER_DATA_KEY);

      // If no cached user data, user has never logged in on this browser
      if (!userData) {
        setIsLoading(false);
        return;
      }

      // Verify auth via httpOnly cookie (sent automatically by browser)
      const response = await authAPI.verifyToken();

      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        // v3.19.0: Capture force_logout info
        setForceLogoutInfo(response.data.force_logout || null);
        // v3.22.0: Capture pending T&C
        setPendingTc(response.data.pending_tc || null);
        // v3.23.0: Capture pending announcements
        setPendingAnnouncements(response.data.pending_announcements || null);
        // Actualizar datos del usuario en localStorage
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(response.data.user));
      } else {
        // Token inválido, limpiar
        clearAuth();
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.login({ email, password });
      
      if (response.data.success) {
        const { user: userData } = response.data.data;

        // Save display data only (token is in httpOnly cookie, not accessible to JS)
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));

        // Actualizar estado
        setUser(userData);
        setIsAuthenticated(true);

        // Check password expiration
        if (userData.password_expired) {
          setPasswordExpired(true);
        }

        // v3.22.1: After login, check pending T&C via verify-token
        // This ensures the TcAcceptanceModal shows immediately on first login
        try {
          const verifyResponse = await authAPI.verifyToken();
          if (verifyResponse.data.success) {
            setForceLogoutInfo(verifyResponse.data.force_logout || null);
            setPendingTc(verifyResponse.data.pending_tc || null);
            setPendingAnnouncements(verifyResponse.data.pending_announcements || null);
          }
        } catch (e) {
          console.error('Error checking pending T&C after login:', e);
        }

        // Use full_name or email as fallback
        const displayName = userData.full_name || userData.email || i18n.t('auth:defaultUser');
        toast.success(i18n.t('auth:welcomeUser', { name: displayName }));
        return { success: true, passwordExpired: !!userData.password_expired };
      }
    } catch (error) {
      const message = error.response?.data?.message || i18n.t('auth:loginError');
      const code = error.response?.data?.code || null;
      // v3.19.0: Don't show toast for FORCE_LOGOUT_ACTIVE - Login will show modal
      if (code !== 'FORCE_LOGOUT_ACTIVE') {
        toast.error(message);
      }
      const loginRestoredInMinutes = error.response?.data?.login_restored_in_minutes || null;
      const loginRestoredInSeconds = error.response?.data?.login_restored_in_seconds || null;
      const templateSubject = error.response?.data?.template_subject || null;
      const templateContent = error.response?.data?.template_content || null;
      return { success: false, error: message, code, loginRestoredInMinutes, loginRestoredInSeconds, templateSubject, templateContent };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await authAPI.register(userData);

      if (response.data.success) {
        toast.success(i18n.t('auth:registerSuccess'));
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || i18n.t('auth:registerError');
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const verifyEmail = async (token, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.verifyEmail({ token, password });
      
      if (response.data.success) {
        toast.success(i18n.t('auth:emailVerified'));
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || i18n.t('auth:verificationError');
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setIsLoading(true);
      const response = await authAPI.forgotPassword({ email });
      
      if (response.data.success) {
        toast.success(i18n.t('auth:forgotPasswordSuccess'));
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || i18n.t('auth:forgotPasswordError');
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setIsLoading(true);
      const response = await authAPI.resetPassword({ token, password });
      
      if (response.data.success) {
        toast.success(i18n.t('auth:resetPasswordSuccess'));
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || i18n.t('auth:resetPasswordError');
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Intentar logout en el servidor
      await authAPI.logout();
    } catch (error) {
      // Continuar con logout local incluso si falla el servidor
      console.error('Error en logout del servidor:', error);
    } finally {
      // Limpiar estado local
      clearAuth();
      toast.success(i18n.t('auth:logoutSuccess'));
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    isAdmin: user?.role_id === 1, // v3.0.0: Only Super Admin (role_id = 1) is admin
    forceLogoutInfo,
    pendingTc,
    setPendingTc,
    pendingAnnouncements,
    setPendingAnnouncements,
    passwordExpired,
    setPasswordExpired,
    login,
    register,
    verifyEmail,
    forgotPassword,
    resetPassword,
    logout,
    updateUser,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;